import { useState, useEffect, useMemo, useCallback, MouseEvent, Fragment } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SEARCH_PARAMS } from '@constants/index';
import { moveScrollTop } from '@utils/index';
import type { l10nType } from '@type/index';

interface IPagingProps {
  endPage: number;
  showPage: number;
  l10n: l10nType;
  onClickCallback?: () => void;
}

const ArrowPrevBtn = ({
  title,
  isDisabled,
  clickCallback,
}: {
  title: string;
  isDisabled: boolean;
  clickCallback: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <div className="search-paging-prev">
      <button type="button" className="search-paging-btn prev" onClick={clickCallback} disabled={isDisabled}>
        <span className="hidden">{title}</span>
      </button>
    </div>
  );
};

const ArrowNextBtn = ({
  title,
  isDisabled,
  clickCallback,
}: {
  title: string;
  isDisabled: boolean;
  clickCallback: (e: MouseEvent<HTMLButtonElement>) => void;
}) => {
  return (
    <div className="search-paging-next">
      <button type="button" className="search-paging-btn next" onClick={clickCallback} disabled={isDisabled}>
        <span className="hidden">{title}</span>
      </button>
    </div>
  );
};

const Paging = ({ endPage, showPage, l10n }: IPagingProps) => {
  const MIN_PAGE = 1;
  const MAX_PAGE = endPage;
  const [searchParams, setSearchParams] = useSearchParams();
  const page = useMemo(() => (searchParams.get(SEARCH_PARAMS.PAGE) ? parseInt(searchParams.get(SEARCH_PARAMS.PAGE)!, 10) : MIN_PAGE), [searchParams]);
  const [isBtnPrevDisabled, setIsBtnPrevDisabled] = useState(false);
  const [isBtnNextDisabled, setIsBtnNextDisabled] = useState(false);
  const btnArr: number[][] = useMemo(
    () =>
      [...Array(endPage)].reduce((acc, cur, index) => {
        if (index % showPage === 0) {
          acc.push([]);
        }
        acc[Math.floor(index / showPage)].push(index + 1);
        return acc;
      }, []),
    [endPage, showPage]
  );
  const btnColumnIndex = useMemo(() => Math.floor((page - 1) / showPage), [page, showPage]);

  useEffect(() => {
    if (endPage <= showPage) {
      !isBtnPrevDisabled && setIsBtnPrevDisabled(true);
      !isBtnNextDisabled && setIsBtnNextDisabled(true);
      return;
    }

    if (endPage <= showPage || btnColumnIndex <= 0) {
      !isBtnPrevDisabled && setIsBtnPrevDisabled(true);
      isBtnNextDisabled && setIsBtnNextDisabled(false);
      return;
    }

    if (endPage <= showPage || btnColumnIndex >= Math.floor(endPage / showPage)) {
      isBtnPrevDisabled && setIsBtnPrevDisabled(false);
      !isBtnNextDisabled && setIsBtnNextDisabled(true);
      return;
    }

    isBtnPrevDisabled && setIsBtnPrevDisabled(false);
    isBtnNextDisabled && setIsBtnNextDisabled(false);
  }, [page]);

  const routeClickCallback = useCallback(() => {
    moveScrollTop();
  }, []);

  const clickCallback = useCallback(
    (index: number) => {
      routeClickCallback();
      searchParams.set(SEARCH_PARAMS.PAGE, `${index}`);
      setSearchParams(searchParams);
    },
    [searchParams]
  );

  const btnPrevClickCallback = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const prevPage = page - showPage;
      routeClickCallback();
      searchParams.set(SEARCH_PARAMS.PAGE, `${prevPage < MIN_PAGE ? MIN_PAGE : prevPage}`);
      setSearchParams(searchParams);
    },
    [searchParams]
  );

  const btnNextClickCallback = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      const nextPage = page + showPage;
      routeClickCallback();
      searchParams.set(SEARCH_PARAMS.PAGE, `${nextPage > MAX_PAGE ? MAX_PAGE : nextPage}`);
      setSearchParams(searchParams);
    },
    [searchParams]
  );

  return (
    <div className="search-paging">
      {endPage > showPage && !isBtnPrevDisabled ? (
        <ArrowPrevBtn title={l10n?.template?.prev} isDisabled={isBtnPrevDisabled} clickCallback={btnPrevClickCallback} />
      ) : (
        <Fragment />
      )}
      <ul className="search-paging-list">
        {btnArr[btnColumnIndex]?.map((v, i) => (
          <li key={v}>
            <button type="button" className={`search-paging-btn ${page === v ? 'active' : ''}`} onClick={(e) => clickCallback(v)}>
              {v}
            </button>
          </li>
        ))}
      </ul>
      {endPage > showPage && !isBtnNextDisabled ? (
        <ArrowNextBtn title={l10n?.template?.next} isDisabled={isBtnNextDisabled} clickCallback={btnNextClickCallback} />
      ) : (
        <Fragment />
      )}
    </div>
  );
};

export default Paging;
